import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginData } from '../types/auth';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

export function LoginForm({ onSuccess, onSwitchToRegister }: LoginFormProps) {
  const [formData, setFormData] = useState<LoginData>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string>('');
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 로그인 후 돌아갈 페이지 정보 (ProtectedRoute에서 전달된 정보)
  const from = location.state?.from?.pathname || null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const user = await login(formData);
      
      // 원래 접속하려던 페이지가 있으면 그곳으로, 없으면 역할별 기본 페이지로
      if (from && from !== 'login') {
        navigate(from, { replace: true });
      } else {
        // 역할별로 다른 페이지로 리다이렉션
        if (user.role === 'collector') {
          navigate('/sslmo/collector/dashboard', { replace: true });
        } else if (user.role === 'admin') {
          navigate('/sslmo/admin', { replace: true });
        } else {
          navigate('/sslmo/dashboard', { replace: true });
        }
      }
      
      // onSuccess 콜백이 있으면 실행 (선택사항)
      onSuccess?.();
    } catch (err: any) {
      setError(
        err.response?.data?.detail || 
        '로그인 중 오류가 발생했습니다. 다시 시도해주세요.'
      );
    }
  };

  return (
    <div className="login-form">
      <h2>로그인</h2>
      
      {from && (
        <p className="redirect-notice">
          로그인 후 요청하신 페이지로 이동합니다.
        </p>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">이메일</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={isLoading}
            autoComplete="email"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">비밀번호</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            disabled={isLoading}
            autoComplete="current-password"
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <button type="submit" disabled={isLoading}>
          {isLoading ? '로그인 중...' : '로그인'}
        </button>
      </form>

      <div className="form-footer">
        <p>
          계정이 없으신가요?{' '}
          <button
            type="button"
            className="link-button"
            onClick={onSwitchToRegister}
            disabled={isLoading}
          >
            회원가입
          </button>
        </p>
      </div>
    </div>
  );
}